import React, { useState, useEffect, useCallback, useRef } from 'react';
import { User, Contact, Interaction, PendingInteraction, NotificationSettings } from './types';
import { recalculateAllContacts, calculateHealthScore } from './lib/healthScore';
import { setBadgeCount } from './lib/utils';
import {
  getToken,
  clearToken,
  contactsApi,
  interactionsApi,
  authApi,
  ApiContact,
  ApiInteraction,
  authApi as authService,
  ApiError,
} from './lib/api';
import { showToast } from './lib/toast';
import {
  getCached,
  setCached,
  clearCache,
  clearQueue,
  enqueue,
  getQueueLength,
} from './lib/offlineStore';
import { isNetworkError, replayQueue, replayOnMount } from './lib/offlineSync';
import { clearModuleCaches, invalidateAfterMutation } from './lib/moduleCache';

declare global {
  interface Window {
    __archivedContact?: Contact;
    __undoToken?: string;
  }
}

const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  dailyReminders: true,
  birthdayReminders: true,
  weeklySummary: true,
  overdueAlerts: true,
  quietHours: { enabled: false, start: '22:00', end: '08:00' },
};

// ─── Mappers: API → Frontend types ──────────────────────────────────────────

function mapApiContact(c: ApiContact): Contact {
  return {
    id: String(c.id),
    name: c.name,
    photoUrl: c.photoUrl,
    status: c.status,
    healthScore: c.healthScore ?? -1,
    lastInteractionDate: c.lastInteractionDate || '',
    lastInteractionType: c.lastInteractionType as Contact['lastInteractionType'],
    overdueDays: Math.max(0, c.daysSinceContact - (c.contactFrequencyDays || 30)),
    birthday: c.birthday ? c.birthday.split('T')[0] : undefined,
    tags: c.tags || [],
    yearMet: c.yearMet ? String(c.yearMet) : undefined,
    relationshipType: c.relationshipType || 'Friend',
    innerCircle: c.innerCircle,
    contactFrequencyDays: c.contactFrequencyDays,
    communicationMethods: c.communicationChannels,
    preferredCommMethod: c.preferredCommMethod,
  };
}

function mapApiInteraction(i: ApiInteraction): Interaction {
  return {
    id: String(i.id),
    contactId: String(i.contactId),
    type: i.interactionType as Interaction['type'],
    date: i.interactionDate?.split('T')[0] || '',
    notes: i.notes,
    rating: i.rating as Interaction['rating'],
    attachments: i.attachments && i.attachments.length > 0 ? i.attachments : undefined,
  };
}

// ─── State ──────────────────────────────────────────────────────────────────

interface SyncPending {
  contacts: Contact[];
  interactions: Interaction[];
  weeklyGoal?: number;
}

interface AppState {
  user: User | null;
  contacts: Contact[];
  interactions: Interaction[];
  weeklyGoalTarget: number;
  pendingInteraction?: PendingInteraction | null;
  isGuestMode: boolean;
  notificationSettings: NotificationSettings;
  isStorageFull: boolean;
  pendingDeleteContactId: string | null;
  pendingDeleteContactName: string | null;
  loading: boolean;
  syncPending?: SyncPending | null;
  offlineQueueLength: number;
}

const AppContext = React.createContext<
  | {
      state: AppState;
      setUser: (user: User | null, opts?: { skipSync?: boolean }) => void;
      setWeeklyGoalTarget: (target: number) => void;
      addInteraction: (interaction: Omit<Interaction, 'id'>, files?: File[]) => void;
      removeInteraction: (id: string) => void;
      addContact: (contact: Omit<Contact, 'id'>) => void;
      updateContact: (id: string, updates: Partial<Omit<Contact, 'id'>>) => void;
      deleteContact: (id: string) => void;
      commitDeleteContact: (id: string) => void;
      restoreContact: (id: string) => void;
      getContactInteractions: (contactId: string) => Interaction[];
      setPendingInteraction: (interaction: PendingInteraction | null) => void;
      clearPendingInteraction: () => void;
      updateNotificationSettings: (settings: Partial<NotificationSettings>) => void;
      logout: () => void;
      dismissStorageWarning: () => void;
      forceStorageFull: () => void;
      refreshContacts: () => Promise<void>;
      refreshInteractions: (contactId?: string) => Promise<void>;
      clearSyncPending: (freshData?: { contacts: unknown[]; interactions: unknown[] }) => void;
    }
  | undefined
>(undefined);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AppState>(() => {
    const token = getToken();
    const saved = localStorage.getItem('nudge_state_v2');

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          user: parsed.user || null,
          contacts: parsed.contacts?.length > 0 ? recalculateAllContacts(parsed.contacts) : [],
          interactions: parsed.interactions || [],
          weeklyGoalTarget: parsed.weeklyGoalTarget || 5,
          isGuestMode: !parsed.user || parsed.user.isGuest,
          pendingInteraction: (() => {
            const p = parsed.pendingInteraction;
            if (!p || !p.expiresAt || p.expiresAt < Date.now()) return null;
            return p;
          })(),
          notificationSettings: parsed.notificationSettings || DEFAULT_NOTIFICATION_SETTINGS,
          isStorageFull: false,
          pendingDeleteContactId: null,
          pendingDeleteContactName: null,
          loading: !!token, // if we have a token, we'll be loading data
          offlineQueueLength: 0,
        };
      } catch {
        // corrupted localStorage
      }
    }

    // No saved state — fresh start
    return {
      user: null,
      contacts: [],
      interactions: [],
      weeklyGoalTarget: 5,
      pendingInteraction: null,
      isGuestMode: true,
      notificationSettings: DEFAULT_NOTIFICATION_SETTINGS,
      isStorageFull: false,
      pendingDeleteContactId: null,
      pendingDeleteContactName: null,
      loading: !!token,
      offlineQueueLength: 0,
    };
  });

  const isGuest = state.isGuestMode;
  const fetchedRef = useRef(false);
  const syncingRef = useRef(false); // Prevent persistence effect from clearing guest data during sync

  // ─── Fetch data from API on auth ───────────────────────────────────────────

  const refreshContacts = useCallback(async () => {
    if (!getToken()) return;
    try {
      const res = await contactsApi.list({ limit: 100 });
      const contacts = res.data.map(mapApiContact);
      setState((prev) => ({ ...prev, contacts }));
      setCached('contacts:list', contacts);
    } catch {
      // silently fail — data stays as-is
    }
  }, []);

  const refreshInteractions = useCallback(async (contactId?: string) => {
    if (!getToken()) return;
    try {
      const params: { limit: number; contact_id?: number } = { limit: 100 };
      if (contactId) params.contact_id = Number(contactId);
      const res = await interactionsApi.list(params);
      const interactions = res.data.map(mapApiInteraction);
      if (contactId) {
        // Merge: replace interactions for this contact, keep others
        setState((prev) => {
          const merged = [
            ...prev.interactions.filter((i) => i.contactId !== contactId),
            ...interactions,
          ];
          setCached('interactions:list', merged);
          return { ...prev, interactions: merged };
        });
      } else {
        setState((prev) => ({ ...prev, interactions }));
        setCached('interactions:list', interactions);
      }
    } catch {
      // silently fail
    }
  }, []);

  // On mount: if we have a token, fetch user profile + data
  useEffect(() => {
    if (fetchedRef.current) return;
    const token = getToken();
    if (!token) return;
    fetchedRef.current = true;

    (async () => {
      // Try IDB cache first for instant load
      const [cachedContacts, cachedInteractions] = await Promise.all([
        getCached<Contact[]>('contacts:list'),
        getCached<Interaction[]>('interactions:list'),
      ]);
      if (cachedContacts || cachedInteractions) {
        setState((prev) => ({
          ...prev,
          contacts: cachedContacts?.data ?? prev.contacts,
          interactions: cachedInteractions?.data ?? prev.interactions,
          loading: false,
        }));
      }

      try {
        const { user: profile } = await authApi.me();
        setState((prev) => ({
          ...prev,
          user: {
            id: profile.id,
            name: profile.name,
            email: profile.email,
            isGuest: false,
            roles: profile.roles,
            timezone: profile.timezone,
            weeklyGoalTarget: profile.weeklyGoalTarget,
            photoUrl: profile.photoUrl,
            birthday: profile.birthday,
            referralCode: profile.referralCode,
          },
          weeklyGoalTarget: profile.weeklyGoalTarget || 5,
          notificationSettings: profile.notificationSettings
            ? {
                dailyReminders: profile.notificationSettings.dailyReminders,
                birthdayReminders: profile.notificationSettings.birthdayReminders,
                weeklySummary: profile.notificationSettings.weeklySummary,
                overdueAlerts: profile.notificationSettings.overdueAlerts,
                quietHours: profile.notificationSettings.quietHours || {
                  enabled: false,
                  start: '22:00',
                  end: '08:00',
                },
              }
            : DEFAULT_NOTIFICATION_SETTINGS,
          isGuestMode: false,
        }));

        // Fetch fresh contacts + interactions in background
        const [contactsRes, interactionsRes] = await Promise.all([
          contactsApi.list({ limit: 100 }),
          interactionsApi.list({ limit: 100 }),
        ]);

        const contacts = contactsRes.data.map(mapApiContact);
        const interactions = interactionsRes.data.map(mapApiInteraction);

        setState((prev) => ({
          ...prev,
          contacts,
          interactions,
          loading: false,
        }));

        // Update IDB cache with fresh data
        setCached('contacts:list', contacts);
        setCached('interactions:list', interactions);

        // Sync timezone if needed
        const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (profile.timezone !== localTz) {
          authService.updateTimezone(localTz).catch(() => {});
        }

        // Request push notification permission if not yet granted, or re-register token
        import('./lib/firebase').then(({ requestNotificationPermission, refreshPushToken }) => {
          if (Notification.permission === 'granted') {
            refreshPushToken().catch(() => {});
          } else if (Notification.permission === 'default') {
            requestNotificationPermission().catch(() => {});
          }
        });
      } catch {
        // Token invalid or offline — if we loaded from cache, stay with that data
        // Otherwise clear and show welcome
        setState((prev) => {
          if (prev.contacts.length > 0) {
            // Have cached data — just stop loading
            return { ...prev, loading: false, isGuestMode: false };
          }
          clearToken();
          return {
            ...prev,
            user: null,
            isGuestMode: true,
            loading: false,
          };
        });
      }
    })();
  }, []);

  // Listen for unauthorized events (token expired mid-session)
  useEffect(() => {
    const handler = () => {
      clearCache();
      clearQueue();
      clearModuleCaches();
      setState((prev) => ({
        ...prev,
        user: null,
        isGuestMode: true,
        contacts: [],
        interactions: [],
        loading: false,
      }));
    };
    window.addEventListener('nudge:unauthorized', handler);
    return () => window.removeEventListener('nudge:unauthorized', handler);
  }, []);

  // ─── Persist guest state to localStorage ───────────────────────────────────

  const dismissStorageWarning = () => {
    setState((prev) => ({ ...prev, isStorageFull: false }));
  };

  const forceStorageFull = () => {
    setState((prev) => ({ ...prev, isStorageFull: true }));
  };

  useEffect(() => {
    // Don't persist after logout (no user = nothing to save)
    if (!state.user) return;

    // Only persist to localStorage in guest mode
    if (!isGuest) {
      // Don't overwrite localStorage while sync is in progress —
      // guest data must survive until sync completes
      if (syncingRef.current) return;

      // For authenticated users, just save user + UI preferences
      try {
        localStorage.setItem(
          'nudge_state_v2',
          JSON.stringify({
            user: state.user,
            weeklyGoalTarget: state.weeklyGoalTarget,
            notificationSettings: state.notificationSettings,
            pendingInteraction: state.pendingInteraction,
            // Don't persist contacts/interactions — fetched from API
            contacts: [],
            interactions: [],
          }),
        );
      } catch {
        // ignore
      }
      return;
    }

    const serialized = JSON.stringify(state);
    if (serialized.length > 4500000 && !state.isStorageFull) {
      setState((prev) => ({ ...prev, isStorageFull: true }));
      return;
    }

    try {
      localStorage.setItem('nudge_state_v2', serialized);
    } catch (e) {
      if (
        e instanceof DOMException &&
        (e.code === 22 || e.code === 1014 || e.name === 'QuotaExceededError')
      ) {
        setState((prev) => ({ ...prev, isStorageFull: true }));
      }
    }
  }, [state, isGuest]);

  // ─── Badge count (overdue contacts) ─────────────────────────────────────────

  useEffect(() => {
    const overdueCount = state.contacts.filter((c) => c.status === 'overdue').length;
    setBadgeCount(overdueCount);
  }, [state.contacts]);

  // ─── Offline queue replay ──────────────────────────────────────────────────

  useEffect(() => {
    if (isGuest) return;

    const replayCallbacks = {
      refreshContacts,
      refreshInteractions,
      onQueueLengthChange: (len: number) =>
        setState((prev) => ({ ...prev, offlineQueueLength: len })),
    };

    // Replay any queued mutations from previous sessions
    replayOnMount(replayCallbacks);

    // Replay when connectivity is restored
    const handleOnline = () => {
      replayQueue(replayCallbacks);
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [isGuest, refreshContacts, refreshInteractions]);

  // ─── Contact CRUD ──────────────────────────────────────────────────────────

  const addContact = useCallback(
    (contactData: Omit<Contact, 'id'>) => {
      const token = getToken();

      if (!token) {
        const newContact: Contact = {
          ...contactData,
          id: Math.random().toString(36).substr(2, 9),
        };
        setState((prev) => ({
          ...prev,
          contacts: recalculateAllContacts([...prev.contacts, newContact]),
        }));
        return;
      }

      // Optimistic: add to state immediately so user sees it
      const tempId = 'temp_' + Math.random().toString(36).substr(2, 9);
      const optimisticContact: Contact = { ...contactData, id: tempId };
      setState((prev) => ({
        ...prev,
        contacts: [...prev.contacts, optimisticContact],
      }));

      const apiPayload = {
        name: contactData.name,
        communication_channels: contactData.communicationMethods,
        photo_url: contactData.photoUrl,
        relationship_type: contactData.relationshipType,
        contact_frequency_days: contactData.contactFrequencyDays,
        birthday: contactData.birthday || undefined,
        tags: contactData.tags?.length ? contactData.tags : undefined,
        year_met: contactData.yearMet ? Number(contactData.yearMet) : undefined,
        inner_circle: contactData.innerCircle,
        preferred_comm_method: contactData.preferredCommMethod,
      };

      contactsApi
        .create(apiPayload)
        .then((apiContact) => {
          setState((prev) => ({
            ...prev,
            contacts: prev.contacts.map((c) => (c.id === tempId ? mapApiContact(apiContact) : c)),
          }));
          invalidateAfterMutation();
        })
        .catch((err) => {
          if (isNetworkError(err)) {
            enqueue({ type: 'addContact', payload: apiPayload, tempId });
            getQueueLength().then((len) =>
              setState((prev) => ({ ...prev, offlineQueueLength: len })),
            );
          } else {
            console.error('[addContact] API failed:', err);
            showToast(
              err instanceof ApiError && err.message
                ? err.message
                : 'Failed to save contact. Please try again.',
            );
          }
        });
    },
    [], // No dependency on isGuest — token is checked directly
  );

  const updateContact = useCallback((id: string, updates: Partial<Omit<Contact, 'id'>>) => {
    const token = getToken();

    // Optimistic update
    setState((prev) => {
      const updatedContacts = prev.contacts.map((c) => (c.id === id ? { ...c, ...updates } : c));
      return {
        ...prev,
        contacts: !token ? recalculateAllContacts(updatedContacts) : updatedContacts,
      };
    });

    if (token) {
      const apiUpdates: Record<string, unknown> = {};
      if (updates.name !== undefined) apiUpdates.name = updates.name;
      if (updates.communicationMethods !== undefined)
        apiUpdates.communication_channels = updates.communicationMethods;
      if (updates.photoUrl !== undefined) apiUpdates.photo_url = updates.photoUrl;
      if (updates.relationshipType !== undefined)
        apiUpdates.relationship_type = updates.relationshipType;
      if (updates.contactFrequencyDays !== undefined)
        apiUpdates.contact_frequency_days = updates.contactFrequencyDays;
      if (updates.birthday !== undefined) apiUpdates.birthday = updates.birthday || null;
      if (updates.tags !== undefined) apiUpdates.tags = updates.tags;
      if (updates.yearMet !== undefined)
        apiUpdates.year_met = updates.yearMet ? Number(updates.yearMet) : null;
      if (updates.innerCircle !== undefined) apiUpdates.inner_circle = updates.innerCircle;
      if (updates.preferredCommMethod !== undefined)
        apiUpdates.preferred_comm_method = updates.preferredCommMethod;

      contactsApi
        .update(Number(id), apiUpdates)
        .then((apiContact) => {
          setState((prev) => ({
            ...prev,
            contacts: prev.contacts.map((c) => (c.id === id ? mapApiContact(apiContact) : c)),
          }));
          invalidateAfterMutation();
        })
        .catch((err) => {
          if (isNetworkError(err)) {
            enqueue({ type: 'updateContact', payload: { id: Number(id), updates: apiUpdates } });
            getQueueLength().then((len) =>
              setState((prev) => ({ ...prev, offlineQueueLength: len })),
            );
          } else {
            console.error('[updateContact] API error:', err);
            showToast(
              err instanceof ApiError && err.message
                ? `Update failed: ${err.message}`
                : 'Failed to update contact. Please try again.',
            );
          }
        });
    }
  }, []);

  const deleteContact = useCallback((id: string) => {
    setState((prev) => {
      const contactToHide = prev.contacts.find((c) => c.id === id);
      if (!contactToHide) return prev;
      window.__archivedContact = contactToHide;
      return {
        ...prev,
        contacts: prev.contacts.filter((c) => c.id !== id),
        pendingDeleteContactId: id,
        pendingDeleteContactName: contactToHide.name,
      };
    });

    if (getToken()) {
      contactsApi
        .delete(Number(id))
        .then((res) => {
          window.__undoToken = res.undoToken;
          invalidateAfterMutation();
        })
        .catch((err) => {
          if (isNetworkError(err)) {
            enqueue({ type: 'deleteContact', payload: { id: Number(id) } });
            getQueueLength().then((len) =>
              setState((prev) => ({ ...prev, offlineQueueLength: len })),
            );
            return;
          }
          showToast(
            err instanceof ApiError && err.message
              ? err.message
              : 'Failed to delete contact. It has been restored.',
          );
          const restored = window.__archivedContact;
          if (restored) {
            setState((prev) => ({
              ...prev,
              contacts: [...prev.contacts, restored],
              pendingDeleteContactId: null,
              pendingDeleteContactName: null,
            }));
            delete window.__archivedContact;
          }
        });
    }
  }, []);

  const commitDeleteContact = useCallback((_id: string) => {
    setState((prev) => ({
      ...prev,
      pendingDeleteContactId: null,
      pendingDeleteContactName: null,
    }));
    delete window.__archivedContact;
    delete window.__undoToken;
  }, []);

  const restoreContact = useCallback((id: string) => {
    const restored = window.__archivedContact;
    const undoToken = window.__undoToken;
    const token = getToken();

    if (!restored || restored.id !== id) {
      setState((prev) => ({
        ...prev,
        pendingDeleteContactId: null,
        pendingDeleteContactName: null,
      }));
      return;
    }

    setState((prev) => ({
      ...prev,
      contacts: !token
        ? recalculateAllContacts([...prev.contacts, restored])
        : [...prev.contacts, restored],
      pendingDeleteContactId: null,
      pendingDeleteContactName: null,
    }));

    delete window.__archivedContact;

    if (token && undoToken) {
      contactsApi
        .restore(Number(id), undoToken)
        .then((apiContact) => {
          setState((prev) => ({
            ...prev,
            contacts: prev.contacts.map((c) => (c.id === id ? mapApiContact(apiContact) : c)),
          }));
        })
        .catch((err) => {
          showToast(
            err instanceof ApiError && err.message
              ? err.message
              : 'Failed to restore contact. Please try again.',
          );
        });
      delete window.__undoToken;
    }
  }, []);

  // ─── Interaction CRUD ──────────────────────────────────────────────────────

  const addInteraction = useCallback(
    (interactionData: Omit<Interaction, 'id'>, files?: File[]) => {
      if (!getToken()) {
        const saveGuestInteraction = (attachments?: string[]) => {
          const newInteraction: Interaction = {
            ...interactionData,
            id: Math.random().toString(36).substr(2, 9),
            attachments,
          };
          setState((prev) => {
            const updatedContacts = prev.contacts.map((c) => {
              if (c.id === interactionData.contactId) {
                const { score, status, overdueDays } = calculateHealthScore(
                  interactionData.date,
                  c.contactFrequencyDays,
                );
                return {
                  ...c,
                  lastInteractionDate: interactionData.date,
                  status,
                  healthScore: score,
                  overdueDays,
                };
              }
              return c;
            });
            return {
              ...prev,
              contacts: updatedContacts,
              interactions: [newInteraction, ...prev.interactions],
            };
          });
        };

        // Convert files to base64 for localStorage
        if (files && files.length > 0) {
          Promise.all(
            files.map(
              (file) =>
                new Promise<string>((resolve) => {
                  const reader = new FileReader();
                  reader.onload = (e) => resolve(e.target?.result as string);
                  reader.readAsDataURL(file);
                }),
            ),
          ).then((base64s) => saveGuestInteraction(base64s));
        } else {
          saveGuestInteraction();
        }
        return;
      }

      // Optimistic: add interaction to state immediately
      const tempId = 'temp_' + Math.random().toString(36).substr(2, 9);
      const optimisticInteraction: Interaction = {
        ...interactionData,
        id: tempId,
      };
      setState((prev) => ({
        ...prev,
        interactions: [optimisticInteraction, ...prev.interactions],
      }));

      // API call
      interactionsApi
        .create({
          contact_id: Number(interactionData.contactId),
          interaction_type: interactionData.type,
          interaction_date: interactionData.date || undefined,
          notes: interactionData.notes,
          rating: interactionData.rating,
          attachments: files,
        })
        .then((apiInteraction) => {
          const mapped = mapApiInteraction(apiInteraction);
          setState((prev) => ({
            ...prev,
            interactions: prev.interactions.map((i) => (i.id === tempId ? mapped : i)),
          }));
          invalidateAfterMutation();
          // Refresh the contact to get updated health score
          refreshContacts();
        })
        .catch((err) => {
          if (isNetworkError(err)) {
            const queuePayload = {
              contact_id: Number(interactionData.contactId),
              interaction_type: interactionData.type,
              interaction_date: interactionData.date || undefined,
              notes: interactionData.notes,
              rating: interactionData.rating,
            };
            const dependsOnTempId = interactionData.contactId.startsWith('temp_')
              ? interactionData.contactId
              : undefined;

            // Convert photos to base64 for IDB queue (same as guest mode)
            if (files && files.length > 0) {
              Promise.all(
                files.map(
                  (file) =>
                    new Promise<string>((resolve) => {
                      const reader = new FileReader();
                      reader.onload = (e) => resolve(e.target?.result as string);
                      reader.readAsDataURL(file);
                    }),
                ),
              ).then((base64s) => {
                // Update optimistic interaction with base64 previews
                setState((prev) => ({
                  ...prev,
                  interactions: prev.interactions.map((i) =>
                    i.id === tempId ? { ...i, attachments: base64s } : i,
                  ),
                }));
                enqueue({
                  type: 'addInteraction',
                  payload: { ...queuePayload, base64Attachments: base64s },
                  dependsOnTempId,
                });
                getQueueLength().then((len) =>
                  setState((prev) => ({ ...prev, offlineQueueLength: len })),
                );
              });
            } else {
              enqueue({
                type: 'addInteraction',
                payload: queuePayload,
                dependsOnTempId,
              });
              getQueueLength().then((len) =>
                setState((prev) => ({ ...prev, offlineQueueLength: len })),
              );
            }
          } else {
            // Remove optimistic interaction on real error
            setState((prev) => ({
              ...prev,
              interactions: prev.interactions.filter((i) => i.id !== tempId),
            }));
            showToast(
              err instanceof ApiError && err.message
                ? err.message
                : 'Failed to save interaction. Please try again.',
            );
          }
        });
    },
    [refreshContacts],
  );

  const removeInteraction = useCallback(
    (id: string) => {
      setState((prev) => ({
        ...prev,
        interactions: prev.interactions.filter((i) => i.id !== id),
      }));

      if (getToken()) {
        interactionsApi
          .delete(Number(id))
          .then(() => {
            invalidateAfterMutation();
            refreshContacts();
          })
          .catch((err) => {
            if (isNetworkError(err)) {
              enqueue({ type: 'deleteInteraction', payload: { id: Number(id) } });
              getQueueLength().then((len) =>
                setState((prev) => ({ ...prev, offlineQueueLength: len })),
              );
            } else {
              showToast(
                err instanceof ApiError && err.message
                  ? err.message
                  : 'Failed to delete interaction. Please try again.',
              );
            }
          });
      }
    },
    [refreshContacts],
  );

  // ─── Other actions ─────────────────────────────────────────────────────────

  const getContactInteractions = useCallback(
    (contactId: string) => {
      return state.interactions.filter((i) => i.contactId === contactId);
    },
    [state.interactions],
  );

  const setUser = useCallback((user: User | null, opts?: { skipSync?: boolean }) => {
    const wasGuest = !user || user.isGuest;

    // Read guest data from localStorage BEFORE switching to auth mode
    // (setState is batched in React 18, so we can't rely on capturing from updater)
    let guestContacts: Contact[] = [];
    let guestInteractions: Interaction[] = [];
    let guestWeeklyGoal: number | undefined;
    if (!wasGuest && !opts?.skipSync) {
      try {
        const saved = localStorage.getItem('nudge_state_v2');
        if (saved) {
          const parsed = JSON.parse(saved);
          const isGuest = !parsed.user || parsed.user.isGuest;
          if (isGuest) {
            guestContacts = (parsed.contacts || []).filter((c: Contact) => c.id && c.name);
            guestInteractions = parsed.interactions || [];
            guestWeeklyGoal = parsed.weeklyGoalTarget !== 5 ? parsed.weeklyGoalTarget : undefined;
          }
        }
      } catch {
        // Ignore parse errors
      }
    }

    const hasGuestData = guestContacts.length > 0 || guestInteractions.length > 0;

    // If there's guest data to sync, set syncPending so SyncScreen takes over
    // Block persistence effect from clearing localStorage until sync completes
    if (hasGuestData) syncingRef.current = true;

    setState((prev) => ({
      ...prev,
      user,
      isGuestMode: wasGuest,
      syncPending: hasGuestData
        ? { contacts: guestContacts, interactions: guestInteractions, weeklyGoal: guestWeeklyGoal }
        : null,
    }));

    // When a user just logged in with NO guest data, fetch profile + data
    if (!wasGuest && getToken() && !fetchedRef.current && !hasGuestData) {
      fetchedRef.current = true;

      const fetchData = async () => {
        try {
          const [profileRes, contactsRes, interactionsRes] = await Promise.all([
            authApi.me(),
            contactsApi.list({ limit: 100 }),
            interactionsApi.list({ limit: 100 }),
          ]);
          const profile = profileRes.user;
          setState((prev) => ({
            ...prev,
            user: {
              ...prev.user!,
              weeklyGoalTarget: profile.weeklyGoalTarget,
              timezone: profile.timezone,
              photoUrl: profile.photoUrl,
              birthday: profile.birthday,
              referralCode: profile.referralCode,
            },
            weeklyGoalTarget: profile.weeklyGoalTarget || 5,
            contacts: contactsRes.data.map(mapApiContact),
            interactions: interactionsRes.data.map(mapApiInteraction),
            loading: false,
          }));
        } catch (err) {
          console.error('Failed to fetch data after login:', err);
          setState((prev) => ({ ...prev, loading: false }));
        }
      };

      fetchData();
    }
  }, []);

  // Called by SyncScreen when sync completes (or skipped)
  const clearSyncPending = useCallback(
    (freshData?: { contacts: unknown[]; interactions: unknown[] }) => {
      syncingRef.current = false;
      fetchedRef.current = true;

      if (freshData) {
        // Sync succeeded — load fresh API data
        setState((prev) => ({
          ...prev,
          contacts: (freshData.contacts as ApiContact[]).map(mapApiContact),
          interactions: (freshData.interactions as ApiInteraction[]).map(mapApiInteraction),
          syncPending: null,
          loading: false,
        }));

        // Fetch full profile (weeklyGoalTarget, timezone, etc.) — fire and forget
        authApi
          .me()
          .then(({ user: profile }) => {
            setState((prev) => ({
              ...prev,
              user: {
                ...prev.user!,
                weeklyGoalTarget: profile.weeklyGoalTarget,
                timezone: profile.timezone,
                photoUrl: profile.photoUrl,
                birthday: profile.birthday,
                referralCode: profile.referralCode,
              },
              weeklyGoalTarget: profile.weeklyGoalTarget || 5,
            }));
          })
          .catch(() => {});
      } else {
        // Sync skipped — restore guest data to state so it persists back to localStorage
        setState((prev) => {
          const pending = prev.syncPending;
          return {
            ...prev,
            contacts: pending?.contacts || prev.contacts,
            interactions: pending?.interactions || prev.interactions,
            isGuestMode: true,
            syncPending: null,
            loading: false,
          };
        });
      }
    },
    [],
  );

  const setWeeklyGoalTarget = useCallback((target: number) => {
    setState((prev) => ({ ...prev, weeklyGoalTarget: target }));
    if (getToken()) {
      authService
        .updateProfile({ weekly_goal_target: target })
        .catch(() => showToast('Failed to save weekly goal.'));
    }
  }, []);

  const setPendingInteraction = useCallback((interaction: PendingInteraction | null) => {
    const withExpiry = interaction
      ? { ...interaction, expiresAt: Date.now() + 24 * 60 * 60 * 1000 }
      : null;
    setState((prev) => ({ ...prev, pendingInteraction: withExpiry }));
  }, []);

  const clearPendingInteraction = useCallback(
    () => setPendingInteraction(null),
    [setPendingInteraction],
  );

  const updateNotificationSettings = useCallback((settings: Partial<NotificationSettings>) => {
    setState((prev) => ({
      ...prev,
      notificationSettings: { ...prev.notificationSettings, ...settings },
    }));

    if (getToken()) {
      const payload: Record<string, unknown> = {};
      if ('dailyReminders' in settings) payload.daily_reminders = settings.dailyReminders;
      if ('birthdayReminders' in settings) payload.birthday_reminders = settings.birthdayReminders;
      if ('weeklySummary' in settings) payload.weekly_summary = settings.weeklySummary;
      if ('overdueAlerts' in settings) payload.overdue_alerts = settings.overdueAlerts;
      if ('quietHours' in settings) payload.quiet_hours = settings.quietHours;

      authService
        .updateProfile({
          notification_settings: payload,
        })
        .catch(() => showToast('Failed to save notification settings.'));
    }
  }, []);

  const logout = useCallback(() => {
    if (getToken()) {
      authService.logout().catch(() => {});
    }
    clearToken();
    setBadgeCount(0);
    fetchedRef.current = false;
    clearCache();
    clearQueue();
    clearModuleCaches();
    setState({
      user: null,
      contacts: [],
      interactions: [],
      weeklyGoalTarget: 5,
      pendingInteraction: null,
      isGuestMode: true,
      notificationSettings: DEFAULT_NOTIFICATION_SETTINGS,
      isStorageFull: false,
      pendingDeleteContactId: null,
      pendingDeleteContactName: null,
      loading: false,
      offlineQueueLength: 0,
    });
    localStorage.removeItem('nudge_state_v2');
  }, []);

  return (
    <AppContext.Provider
      value={{
        state,
        setUser,
        setWeeklyGoalTarget,
        addInteraction,
        removeInteraction,
        addContact,
        updateContact,
        deleteContact,
        commitDeleteContact,
        restoreContact,
        getContactInteractions,
        setPendingInteraction,
        clearPendingInteraction,
        updateNotificationSettings,
        logout,
        dismissStorageWarning,
        forceStorageFull,
        refreshContacts,
        refreshInteractions,
        clearSyncPending,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = React.useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
}
